
import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './App.tsx';
import './index.css';
import { initManager } from './lib/init-manager';
import { AppLoading } from '@/components/ui/app-loading';
import { logVersion, APP_VERSION } from './utils/version';
import { installParentSessionMirror } from './utils/parentEventSession';
import { installStaleAppReload } from './utils/staleAppReload';
import { installDomMutationGuard } from './utils/domMutationGuard';

// Tradução do navegador e extensões que mexem na página não podem derrubar a tela (erro removeChild/insertBefore do React)
installDomMutationGuard();
// Antes de qualquer tela: restaura o pedido em andamento quando a página é aberta sem sessão (ex.: navegador do Instagram)
installParentSessionMirror();
// App antigo guardado no navegador depois de um Publish: recarrega uma vez para pegar a versão nova
installStaleAppReload();

// Initialize application with robust error handling and timeout protection
async function initializeApp() {
  const rootElement = document.getElementById('root');
  if (!rootElement) {
    console.error("❌ Root element not found");
    return;
  }

  // Log version first for debugging
  logVersion();

  
  let timeoutId: ReturnType<typeof setTimeout>;
  let hasTimedOut = false;
  
  try {
    // Create loading screen immediately
    const reactRoot = createRoot(rootElement);
    // Use a blank loading screen first to avoid any layout flash from the static HTML
    reactRoot.render(<div className="min-h-screen bg-background" />);
    
    // Then render the actual loading component
    reactRoot.render(<AppLoading />);
    
    // Set 8 second timeout for initialization - otimizado
    const timeoutPromise = new Promise((_, reject) => {
      timeoutId = setTimeout(() => {
        hasTimedOut = true;
        reject(new Error('Initialization timeout after 8 seconds'));
      }, 8000);
    });
    
    // Race between initialization and timeout
    await Promise.race([
      initManager.initialize(),
      timeoutPromise
    ]);
    
    clearTimeout(timeoutId);
    
    if (hasTimedOut) {
      throw new Error('Initialization timeout');
    }




    
    // Render main app only after successful initialization
    reactRoot.render(<App />);
    
  } catch (error) {
    clearTimeout(timeoutId);
    console.error("❌ Critical error during app initialization:", error);
    
    // Create fallback root if needed
    const reactRoot = createRoot(rootElement);
    
    const handleRetry = () => {
      initManager.reset();
      window.location.reload();
    };
    
    reactRoot.render(
      <AppLoading 
        error={!hasTimedOut} 
        timeout={hasTimedOut}
        onRetry={() => {
          initManager.reset();
          // Recovery mais agressivo - tentar recarregar imediatamente
          setTimeout(() => window.location.reload(), 100);
        }}
      />
    );
  }
}

// Start the application
initializeApp();
